/**
 * Sidebar component
 * Desktop-only navigation sidebar with main app sections
 * Hidden on mobile/tablet (bottom navigation is used instead)
 */

import { Link, useRouterState, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useLogout } from '@/hooks/useAuth';
import type { RootSearchParams } from '@/routes/__root';

interface SidebarProps {
  /** Whether user is an organizer (to show/hide certain nav items) */
  isOrganizer?: boolean;
  /** Current user's name */
  userName?: string;
}

/**
 * Sidebar component
 *
 * Features:
 * - Fixed left sidebar for desktop only (lg breakpoint and above)
 * - Active state highlighting based on current route
 * - Icons with labels for easy recognition
 * - User info at top with logout button
 * - Shows organizer-only items when applicable
 * - Scrollable content area
 *
 * @example
 * ```tsx
 * <Sidebar isOrganizer={user.isOrganizer} userName={user.name} />
 * ```
 */
export function Sidebar({ isOrganizer = false, userName }: SidebarProps) {
  const { t } = useTranslation();
  const router = useRouterState();
  const logout = useLogout();

  // Get root search params to preserve week when navigating (exclude page param)
  const rootSearch = useSearch({ from: '__root__' }) as RootSearchParams;
  const { week, redirect } = rootSearch;
  const navSearch = { week, redirect } as RootSearchParams;

  // Get current pathname
  const currentPath = router.location.pathname;

  // Helper to check if route is active
  const isRouteActive = (to: string) => {
    if (to === '/') {
      // For home, only match exact path
      return currentPath === '/';
    }
    // For other routes, check if current path starts with the route
    return currentPath.startsWith(to);
  };

  // Define navigation items (same as bottom navigation)
  const navItems = [
    {
      to: '/',
      label: t('navigation.home'),
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
          />
        </svg>
      ),
      show: true,
    },
    {
      to: '/availability',
      label: t('navigation.availability'),
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      show: true,
    },
    {
      to: '/weekly-selection',
      label: t('navigation.selection'),
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
          />
        </svg>
      ),
      show: true,
    },
    {
      to: '/menu',
      label: t('navigation.menu'),
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      ),
      show: isOrganizer,
    },
    {
      to: '/users',
      label: t('navigation.users', 'Users'),
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
      ),
      show: isOrganizer,
    },
    {
      to: '/meals',
      label: t('navigation.meals'),
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
      ),
      show: true,
    },
    {
      to: '/profile',
      label: t('navigation.profile'),
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      ),
      show: true,
    },
  ];

  // Filter items based on visibility
  const visibleItems = navItems.filter((item) => item.show);

  return (
    <aside
      className="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:w-64 bg-white border-r border-gray-200 z-40"
      role="navigation"
      aria-label={t('navigation.mainNavigation')}
    >
      {/* User info header */}
      <div className="flex-shrink-0 px-4 py-6 border-b border-gray-200">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center">
              <span className="text-lg font-medium text-white">
                {userName?.charAt(0).toUpperCase()}
              </span>
            </div>
          </div>
          <div className="ml-3 flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{userName}</p>
            {isOrganizer && (
              <p className="text-xs text-primary-600">{t('home.organizer')}</p>
            )}
          </div>
        </div>
      </div>

      {/* Navigation items */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
        {visibleItems.map((item) => {
          const isActive = isRouteActive(item.to);

          return (
            <Link
              key={item.to}
              to={item.to}
              search={navSearch}
              className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive
                  ? 'bg-primary-100 text-primary-900'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              }`}
              aria-label={item.label}
              aria-current={isActive ? 'page' : undefined}
            >
              <span className={isActive ? 'text-primary-600' : 'text-gray-500'}>
                {item.icon}
              </span>
              <span className="ml-3">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Logout button at bottom */}
      <div className="flex-shrink-0 border-t border-gray-200 p-4">
        <button
          onClick={logout}
          className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-100 hover:text-gray-900 transition-colors"
        >
          <svg
            className="w-6 h-6 text-gray-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
            />
          </svg>
          <span className="ml-3">{t('auth.logout')}</span>
        </button>
      </div>
    </aside>
  );
}
